// Payslip PDF Generation Service
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import helper from '@/helper';

class PayslipPDFService {
    /**
     * Generate a professional payslip PDF (Bulletin de Paie)
     * @param {Object} payroll - Payroll object
     * @param {Object} employee - Employee object
     * @param {Object} business - Business object
     * @param {String} action - 'download' or 'view'
     */
    static generatePayslip(payroll, employee, business, action = 'download') {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        let yPos = 20;

        // Parse metadata
        let metadata = {};
        let breakdown = {};
        try {
            metadata = JSON.parse(payroll.metadata || '{}');
            breakdown = metadata.breakdown || {};
        } catch (e) {
            metadata = {};
            breakdown = {};
        }
        const payrollContext = metadata.payroll_context || {};
        const complianceWarnings = [...(metadata.compliance_warnings || [])];
        if (!breakdown.rules_version) {
            complianceWarnings.push('Version des règles légales absente');
        }
        if (!payrollContext.employer_cnss_number || !payrollContext.employee_cnss_number) {
            complianceWarnings.push('Numéros CNSS manquants');
        }
        if (
            payrollContext.paid_days === undefined ||
            (
                payrollContext.sector !== 'agricultural' &&
                payrollContext.regular_hours === undefined
            )
        ) {
            complianceWarnings.push('Jours ou heures payés manquants');
        }
        const isDraftOrIncomplete = payroll.status !== 'paid' || complianceWarnings.length > 0;
        const payslipEmployee = metadata.employee_snapshot || employee || {};
        const payslipBusiness = metadata.business_snapshot || business || {};

        // Parse allowances and deductions
        const allowances = this.parseJSON(payroll.allowances);
        const deductions = this.parseJSON(payroll.deductions);

        if (isDraftOrIncomplete) {
            doc.setFontSize(28);
            doc.setTextColor(235, 215, 215);
            doc.setFont(undefined, 'bold');
            doc.text(
                payroll.status === 'paid' ? 'DONNÉES INCOMPLÈTES' : 'BROUILLON',
                pageWidth / 2,
                145,
                { align: 'center', angle: 45 }
            );
        }

        // Company Name
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text(payslipBusiness.name || 'Company Name', 14, 15);
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text(`Adresse: ${payslipBusiness.address || '-'}`, 14, 21);
        doc.text(`Affiliation CNSS: ${payrollContext.employer_cnss_number || '-'}`, 14, 26);

        // Bulletin de Paie
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('BULLETIN DE PAIE', pageWidth - 14, 16, { align: 'right' });

        // Period
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        const periodText = this.formatPeriod(payroll.month);
        doc.text(periodText, pageWidth - 14, 24, { align: 'right' });
        doc.setFontSize(8);
        doc.text(`Devise: MAD`, pageWidth - 14, 30, { align: 'right' });

        // Reset text color
        doc.setTextColor(0, 0, 0);
        yPos = 42;

        // ============= EMPLOYEE INFO =============
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('INFORMATIONS EMPLOYÉ', 14, yPos);
        yPos += 2;

        doc.setLineWidth(0.5);
        doc.setDrawColor(0, 0, 0);
        doc.line(14, yPos, 80, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const employeeInfo = [
            ['Nom:', payslipEmployee.name || '-'],
            ['Référence:', payslipEmployee.reference || '-'],
            ['N° CNSS:', payrollContext.employee_cnss_number || '-'],
            ['Catégorie:', payrollContext.professional_category || payslipEmployee.position || '-'],
            ['Date d’embauche:', payslipEmployee.hire_date ? helper.formatDate(payslipEmployee.hire_date) : '-'],
            ['Département:', payslipEmployee.department || '-'],
            ['Jours payés:', payrollContext.paid_days !== undefined && payrollContext.paid_days !== null ? String(payrollContext.paid_days) : '-'],
            ['Heures normales:', payrollContext.regular_hours !== undefined && payrollContext.regular_hours !== null ? String(payrollContext.regular_hours) : '-'],
            ['Heures supplémentaires:', payrollContext.overtime_hours ? `${payrollContext.overtime_hours} h × ${this.formatMoney(payrollContext.overtime_rate || 0)}` : '0']
        ];

        employeeInfo.forEach(([label, value]) => {
            doc.setFont(undefined, 'bold');
            doc.text(label, 14, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(value, 50, yPos);
            yPos += 6;
        });

        yPos += 5;

        // ============= SALARY BREAKDOWN TABLE =============
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('DÉTAIL DE LA RÉMUNÉRATION', 14, yPos);
        yPos += 8;

        // Prepare table data
        const tableData = [];

        // Basic Salary
        tableData.push([
            { content: 'Salaire de base', styles: { fontStyle: 'bold' } },
            payrollContext.regular_hours
                ? `${payrollContext.regular_hours} h`
                : '',
            this.formatMoney(payroll.basic_salary)
        ]);

        // Allowances
        if (allowances.length > 0) {
            tableData.push([
                { content: 'PRIMES ET INDEMNITÉS', colSpan: 3, styles: { fontStyle: 'bold' } }
            ]);
            allowances.forEach(allowance => {
                tableData.push([
                    `  ${allowance.description}`,
                    '',
                    this.formatMoney(allowance.amount)
                ]);
            });
        }

        // Gross Salary
        tableData.push([
            { content: 'SALAIRE BRUT', styles: { fontStyle: 'bold' } },
            '',
            { content: this.formatMoney(payroll.gross_salary), styles: { fontStyle: 'bold' } }
        ]);

        // Statutory Deductions
        if (breakdown.statutory_deductions && breakdown.statutory_deductions.length > 0) {
            tableData.push([
                { content: 'COTISATIONS OBLIGATOIRES', colSpan: 3, styles: { fontStyle: 'bold' } }
            ]);

            breakdown.statutory_deductions.forEach(deduction => {
                tableData.push([
                    `  ${deduction.name}`,
                    `(${deduction.type})`,
                    `-${this.formatMoney(deduction.amount)}`
                ]);
            });

        }

        // Custom Deductions
        if (deductions.length > 0) {
            tableData.push([
                { content: 'RETENUES DIVERSES', colSpan: 3, styles: { fontStyle: 'bold' } }
            ]);
            deductions.forEach(deduction => {
                tableData.push([
                    `  ${deduction.description}`,
                    '',
                    `-${this.formatMoney(deduction.amount)}`
                ]);
            });
        }

        // Net Salary
        tableData.push([
            { content: 'SALAIRE NET À PAYER', styles: { fontStyle: 'bold', fontSize: 11 } },
            '',
            { content: this.formatMoney(payroll.net_salary), styles: { fontStyle: 'bold', fontSize: 11 } }
        ]);

        // Generate table
        doc.autoTable({
            startY: yPos,
            head: [['Description', 'Type', 'Montant']],
            body: tableData,
            theme: 'grid',
            styles: {
                fontSize: 9,
                cellPadding: 3
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                fontSize: 10
            },
            columnStyles: {
                0: { cellWidth: 100 },
                1: { cellWidth: 40, halign: 'center' },
                2: { cellWidth: 40, halign: 'right' }
            },
            didParseCell: function (data) {
                // Right align money columns
                if (data.column.index === 2) {
                    data.cell.styles.halign = 'right';
                }
            }
        });

        yPos = doc.lastAutoTable.finalY + 10;

        // ============= EMPLOYER COSTS (if any) =============
        if (breakdown.employer_costs && breakdown.employer_costs.length > 0) {
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('CHARGES PATRONALES', 14, yPos);
            yPos += 5;

            const employerData = breakdown.employer_costs.map(cost => [
                cost.name,
                this.formatMoney(cost.amount)
            ]);

            // Total employer cost
            const totalEmployerCost = breakdown.employer_costs.reduce((sum, cost) => sum + parseFloat(cost.amount), 0);
            employerData.push([
                { content: 'Total Charges Patronales', styles: { fontStyle: 'bold' } },
                { content: this.formatMoney(totalEmployerCost), styles: { fontStyle: 'bold' } }
            ]);

            doc.autoTable({
                startY: yPos,
                body: employerData,
                theme: 'plain',
                styles: {
                    fontSize: 8,
                    cellPadding: 2
                },
                columnStyles: {
                    0: { cellWidth: 120 },
                    1: { cellWidth: 40, halign: 'right' }
                }
            });

            yPos = doc.lastAutoTable.finalY + 10;
        }

        // ============= PAYMENT INFO =============
        if (payroll.status === 'paid' && payroll.payment_date) {
            yPos += 5;
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(`Paiement effectué le: ${helper.formatDate(payroll.payment_date)}`, 14, yPos);
            if (payroll.payment_method) {
                yPos += 5;
                doc.text(`Mode de paiement: ${payroll.payment_method}`, 14, yPos);
            }
        }

        // ============= FOOTER =============
        const footerY = doc.internal.pageSize.getHeight() - 20;
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
            `Date d’édition: ${helper.formatDate(metadata.issued_at || new Date())}`,
            14,
            footerY
        );
        doc.text(
            payroll.status === 'paid' && complianceWarnings.length === 0
                ? 'Bulletin définitif'
                : 'Simulation non définitive',
            pageWidth / 2,
            footerY,
            { align: 'center' }
        );
        doc.text('Document confidentiel', pageWidth - 50, footerY);

        // Save or view
        if (action === 'download') {
            const filename = `Bulletin_Paie_${payslipEmployee.reference || 'EMP'}_${payroll.month}.pdf`;
            doc.save(filename);
        } else {
            // Open in new window for viewing
            window.open(doc.output('bloburl'), '_blank');
        }

        return doc;
    }

    /**
     * Parse JSON safely
     */
    static parseJSON(jsonString) {
        try {
            return JSON.parse(jsonString || '[]');
        } catch (e) {
            return [];
        }
    }

    /**
     * Format money
     */
    static formatMoney(amount) {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD'
        }).format(Number(amount) || 0);
    }

    /**
     * Format period
     */
    static formatPeriod(monthStr) {
        if (!monthStr) return '-';
        const [year, month] = monthStr.split('-');
        const date = new Date(year, month - 1);
        return date.toLocaleString('fr-FR', { month: 'long', year: 'numeric' }).toUpperCase();
    }
}

export default PayslipPDFService;
